import React, {useEffect} from 'react'
import PropTypes from 'prop-types'
import {useQueryClient} from '@tanstack/react-query'
import {useConfig} from '@salesforce/commerce-sdk-react'
import useMultiSite from '../../../hooks/use-multi-site'
import {useNavigationHeaderContent} from '../../../hooks/useScapiCustomApi'
import ModuleContainer from '../../components/module-container'
import {PreviewPaneNotices} from './notices'
import {headerDesktopOnlyMessage, headerInShellMessage} from './diagnostics'

/**
 * MomentVM `leap-header` module.
 *
 * The header lives in the app shell, so this module renders none of its own and
 * writes the previewed content into the `useNavigationHeaderContent` cache the
 * shell header reads. What it renders is a marker holding the module's place in
 * the page: the attributes MomentVM opens its editor from, and the lines saying
 * where the content appears.
 *
 * @param {Object} props - The `leap-header` module content.
 * @returns {JSX.Element} The place-holding marker.
 */
const Header = React.forwardRef((props) => {
    const {anchor, moduleId, moduleType, pageId} = props
    const queryClient = useQueryClient()
    const {organizationId} = useConfig()
    const {
        locale: {id: locale}
    } = useMultiSite()

    useEffect(() => {
        const queryKey = useNavigationHeaderContent.queryKey({organizationId, locale})
        // a request still in flight would land after the write and replace the preview
        queryClient.cancelQueries({queryKey})
        queryClient.setQueryData(queryKey, props)
    }, [queryClient, organizationId, locale, props])

    return (
        <ModuleContainer
            anchor={anchor}
            moduleId={moduleId}
            moduleType={moduleType}
            pageId={pageId}
            background="none"
        >
            <PreviewPaneNotices
                info={[
                    {message: headerInShellMessage()},
                    {message: headerDesktopOnlyMessage(), viewport: 'mobile'}
                ]}
            />
        </ModuleContainer>
    )
})

Header.propTypes = {
    anchor: PropTypes.string,
    moduleId: PropTypes.string,
    moduleType: PropTypes.string,
    pageId: PropTypes.string,
    headerMainLink: PropTypes.object,
    elements: PropTypes.array
}

if (process.env.NODE_ENV !== 'production') {
    Header.displayName = 'Header'
}

export default Header
